import type { ApiSessionData } from '../api/types';
import type { SessionUserInfo } from '../types';

import { DC_IDS, DEBUG, SESSION_EXPORT_FILENAME } from '../config';
import { ACCOUNT_SLOT, loadSlotSession, storeAccountData } from './multiaccount';
import { clearStoredSession, loadStoredSession, storeSession } from './sessions';

type ExportedSessionData = {
  version: string;
  exportDate: number;
  accountSlot: number | undefined;
  sessionData: ApiSessionData;
  userInfo: SessionUserInfo;
};

const AUTH_KEY_REGEX = /^[0-9a-f]+$/i;
const USER_INFO_STRING_FIELDS = ['userId', 'firstName', 'lastName', 'phone', 'avatarUri'] as const;

const EXPORT_EXTENSION = SESSION_EXPORT_FILENAME.includes('.')
  ? SESSION_EXPORT_FILENAME.slice(SESSION_EXPORT_FILENAME.lastIndexOf('.'))
  : '';

export function exportSessionToFile() {
  const sessionData = loadStoredSession();
  if (!sessionData) throw new Error('No active session found');

  const sharedData = loadSlotSession(ACCOUNT_SLOT);
  const exportData: ExportedSessionData = {
    version: APP_VERSION,
    exportDate: Date.now(),
    accountSlot: ACCOUNT_SLOT,
    sessionData,
    userInfo: {
      userId: sharedData?.userId,
      firstName: sharedData?.firstName,
      lastName: sharedData?.lastName,
      phone: sharedData?.phone,
      avatarUri: sharedData?.avatarUri,
      isPremium: sharedData?.isPremium,
    },
  };

  const blob = new Blob([JSON.stringify(exportData, undefined, 2)], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = SESSION_EXPORT_FILENAME;
  link.click();
  URL.revokeObjectURL(url);
}

export function importSessionFromFile() {
  return new Promise<void>((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    if (EXPORT_EXTENSION) input.accept = EXPORT_EXTENSION;

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        reject(new Error('No file selected'));
        return;
      }

      try {
        await applyImportedSession(await file.text());
        resolve();
      } catch (error) {
        reject(error);
      }
    };

    input.click();
  });
}

async function applyImportedSession(fileContent: string) {
  const { sessionData, userInfo } = parseImportedSession(fileContent);

  // Replace, not merge: keys or user info left over from the current account must not survive
  await clearStoredSession(ACCOUNT_SLOT);
  await storeSession(sessionData);
  if (userInfo.userId) {
    await storeAccountData(ACCOUNT_SLOT, userInfo);
  }

  window.location.reload();
}

function parseImportedSession(fileContent: string) {
  const importData = JSON.parse(fileContent) as Partial<ExportedSessionData> | undefined;
  const mainDcId = Number(importData?.sessionData?.mainDcId);
  const rawKeys: Record<string, unknown> = importData?.sessionData?.keys ?? {};

  const keys: Record<number, string> = {};
  Object.entries(rawKeys).forEach(([dcId, key]) => {
    if (!isDcId(Number(dcId)) || typeof key !== 'string' || !AUTH_KEY_REGEX.test(key)) return;
    keys[Number(dcId)] = key;
  });

  if (!isDcId(mainDcId) || !keys[mainDcId]) {
    throw new Error('Invalid session file');
  }

  const rawUserInfo: Record<string, unknown> = importData?.userInfo ?? {};
  const userInfo: SessionUserInfo = {};
  USER_INFO_STRING_FIELDS.forEach((field) => {
    const value = rawUserInfo[field];
    if (typeof value === 'string') userInfo[field] = value;
  });
  if (typeof rawUserInfo.isPremium === 'boolean') userInfo.isPremium = rawUserInfo.isPremium;

  const sessionData: ApiSessionData = {
    mainDcId,
    keys,
    isTest: importData?.sessionData?.isTest === true ? true : undefined,
  };

  return { sessionData, userInfo };
}

function isDcId(dcId: number) {
  return (DC_IDS as readonly number[]).includes(dcId);
}

export async function clearLocalSession() {
  await clearStoredSession(ACCOUNT_SLOT);
  window.location.reload();
}

declare global {
  interface Window {
    saveSession?: typeof exportSessionToFile;
    loadSession?: typeof importSessionFromFile;
    clearLocalSession?: typeof clearLocalSession;
  }
}

export function registerSessionCommands() {
  window.saveSession = exportSessionToFile;
  window.loadSession = importSessionFromFile;
  window.clearLocalSession = clearLocalSession;

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[sessionFiles] saveSession(), loadSession(), clearLocalSession() available');
  }
}
